import type { Payment, PaymentMethod, PaymentPurpose, Policy } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileText, MessageSquare, Plus, Wallet, XCircle } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, Checkbox, EmptyState, ErrorState, Input, LoadingState, Select, Sheet, Textarea, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, shortDate } from "../theme";

/** The drawer: payments taken at the office by day, receipts, and what still has to be posted to the carrier. */

type Row = { payment: Payment; customerName: string; takenByName: string };
type ListResult = { from?: string; to?: string; payments: Row[]; totals: { count: number; amountCents: number; unremittedCents: number; unremittedCount: number; byMethod: Record<string, { count: number; amountCents: number }> } };
const METHODS: PaymentMethod[] = ["cash", "card", "check", "money_order", "other"];
const PURPOSES: PaymentPurpose[] = ["premium", "down_payment", "installment", "reinstatement", "fee", "other"];

export function PaymentsScreen() {
  const t = useT();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [remitRef, setRemitRef] = useState("");
  const q = useQuery({ queryKey: ["payments", day], queryFn: async () => (await callAction<"agency.payment.list", ListResult>("agency.payment.list", { from: day, to: day, includeVoided: true, unremittedOnly: false, limit: 500 })).result });
  const remit = useMutation({
    mutationFn: () => callAction("agency.payment.remit", { paymentIds: selected, reference: remitRef || undefined }),
    onSuccess: () => { toast({ title: t("payments.remitDone"), tone: "success" }); setSelected([]); setRemitRef(""); qc.invalidateQueries({ queryKey: ["payments"] }); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const rows = q.data?.payments ?? [];
  const pending = rows.filter((r) => !r.payment.remittedAt && !r.payment.voidedAt);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("payments.title")}</h1>
          <p className="text-ink/60">{t("payments.subtitle")}</p>
        </div>
        <div className="flex items-end gap-2">
          <Input label={t("payments.day")} type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4" />{t("payments.record")}</Button>
        </div>
      </div>
      <p className="text-xs text-ink/55">{t("payments.notProcessor")}</p>
      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-ink/50">{t("payments.total")}</p><p className="mt-1 text-2xl font-bold">{money(q.data.totals.amountCents)}</p><p className="text-xs text-ink/55">{t("payments.count").replace("{n}", String(q.data.totals.count))}</p></Card>
          <Card className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-ink/50">{t("payments.unremitted")}</p><p className={cn("mt-1 text-2xl font-bold", q.data.totals.unremittedCents && "text-amber-700")}>{money(q.data.totals.unremittedCents)}</p><p className="text-xs text-ink/55">{t("payments.count").replace("{n}", String(q.data.totals.unremittedCount))}</p></Card>
          <Card className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-ink/50">{t("payments.method")}</p><ul className="mt-1 text-sm">{Object.entries(q.data.totals.byMethod).map(([m, v]) => <li key={m} className="flex justify-between"><span>{t(`payments.method.${m}`)}</span><span className="font-semibold">{money(v.amountCents)} <span className="text-ink/45">×{v.count}</span></span></li>)}{!Object.keys(q.data.totals.byMethod).length ? <li className="text-ink/55">—</li> : null}</ul></Card>
        </div>
      ) : null}
      {q.data && rows.length === 0 ? <EmptyState title={t("payments.none")} /> : null}
      {rows.length ? (
        <Card>
          {pending.length ? (
            <div className="flex flex-wrap items-center gap-2 border-b border-ink/5 px-4 py-2">
              <Checkbox label={t("payments.selectAll")} checked={selected.length === pending.length} onChange={(v) => setSelected(v ? pending.map((r) => r.payment.id) : [])} />
              <Input value={remitRef} onChange={(e) => setRemitRef(e.target.value)} placeholder={t("payments.remitRef")} className="sm:w-80" aria-label={t("payments.remitRef")} />
              <Button size="sm" disabled={!selected.length} loading={remit.isPending} onClick={() => remit.mutate()}><CheckCircle2 className="h-4 w-4" />{t("payments.markRemitted")} ({selected.length})</Button>
            </div>
          ) : null}
          <ul className="divide-y divide-ink/5">
            {rows.map((r) => <PaymentRow key={r.payment.id} row={r} selected={selected.includes(r.payment.id)} onSelect={(v) => setSelected((xs) => (v ? [...xs, r.payment.id] : xs.filter((x) => x !== r.payment.id)))} />)}
          </ul>
        </Card>
      ) : null}
      <RecordPaymentSheet open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

export function PaymentRow({ row, selected, onSelect, compact }: { row: Row; selected?: boolean; onSelect?: (v: boolean) => void; compact?: boolean }) {
  const t = useT();
  const { locale } = useLocale();
  const { toast } = useToast();
  const qc = useQueryClient();
  const p = row.payment;
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState("");
  const refresh = () => { for (const k of ["payments", "customer", "policy-history"]) qc.invalidateQueries({ queryKey: [k] }); };
  const openReceipt = useMutation({
    mutationFn: () => callAction<"agency.payment.get", { receiptUrl: string | null }>("agency.payment.get", { paymentId: p.id }),
    onSuccess: (r) => { if (r.result.receiptUrl) window.open(r.result.receiptUrl, "_blank", "noopener"); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const textReceipt = useMutation({
    mutationFn: () => callAction("agency.payment.receipt_text", { paymentId: p.id }),
    onSuccess: () => { toast({ title: t("payments.receiptTexted"), tone: "success" }); refresh(); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const voidIt = useMutation({
    mutationFn: () => callAction("agency.payment.void", { paymentId: p.id, reason }),
    onSuccess: () => { toast({ title: t("payments.voidedToast"), tone: "success" }); setVoiding(false); refresh(); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  return (
    <li className={cn("px-4 py-3", p.voidedAt && "opacity-60")}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {onSelect && !p.remittedAt && !p.voidedAt ? <Checkbox label="" checked={!!selected} onChange={onSelect} /> : null}
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-ink/55">{p.receiptNumber}</span>
            <Link to={`/customers/${p.customerId}`} className="font-bold hover:underline">{row.customerName}</Link>
            <span className="text-lg font-bold">{money(p.amountCents)}</span>
            <Badge>{t(`payments.method.${p.method}`)}{p.reference ? ` · ${p.reference}` : ""}</Badge>
            {p.voidedAt ? <Badge tone="red">{t("payments.voided")}</Badge> : p.remittedAt ? <Badge tone="green">{t("payments.remitted")}</Badge> : <Badge tone="amber">{t("payments.pending")}</Badge>}
          </p>
          <p className="mt-0.5 text-xs text-ink/55">
            {shortDate(p.takenAt, locale)} · {t(`payments.purpose.${p.purpose}`)}{p.carrier ? ` · ${p.carrier}` : ""} · {t("payments.takenBy")} {row.takenByName}
            {p.remittedRef ? ` · ${p.remittedRef}` : ""}{p.voidReason ? ` · ${p.voidReason}` : ""}{p.note && !compact ? ` · ${p.note}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="outline" onClick={() => openReceipt.mutate()} loading={openReceipt.isPending}><FileText className="h-4 w-4" />{t("payments.openReceipt")}</Button>
          {!p.voidedAt ? <Button size="sm" variant="outline" onClick={() => textReceipt.mutate()} loading={textReceipt.isPending}><MessageSquare className="h-4 w-4" />{t("payments.textReceipt")}</Button> : null}
          {!p.voidedAt && !p.remittedAt ? <Button size="sm" variant="ghost" onClick={() => setVoiding((v) => !v)}><XCircle className="h-4 w-4" />{t("payments.void")}</Button> : null}
        </div>
      </div>
      {voiding ? (
        <form className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-red-50 p-2" onSubmit={(e: FormEvent) => { e.preventDefault(); if (reason.trim().length >= 2) voidIt.mutate(); }}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("payments.voidReason")} className="sm:w-80" aria-label={t("payments.voidReason")} />
          <Button size="sm" variant="danger" type="submit" loading={voidIt.isPending} disabled={reason.trim().length < 2}>{t("payments.void")}</Button>
          <Button size="sm" variant="outline" type="button" onClick={() => setVoiding(false)}>{t("common.cancel")}</Button>
        </form>
      ) : null}
    </li>
  );
}

export function RecordPaymentSheet({ open, onClose, customerId, policies }: { open: boolean; onClose: () => void; customerId?: string; policies?: Policy[] }) {
  const t = useT();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({ customerQuery: "", customerId: customerId ?? "", policyId: "", amount: "", method: "cash" as PaymentMethod, purpose: "premium" as PaymentPurpose, reference: "", note: "" });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const search = useQuery({ queryKey: ["customers", "payment-search", form.customerQuery], enabled: !customerId && form.customerQuery.trim().length >= 2, queryFn: async () => (await callAction<"agency.customer.search", Array<{ id: string; name: string; phone?: string }>>("agency.customer.search", { q: form.customerQuery, limit: 8 })).result });
  const pols = useQuery({ queryKey: ["customer", "policies-for-payment", form.customerId], enabled: !policies && !!form.customerId, queryFn: async () => (await callAction<"agency.customer.get", { policies: Policy[] }>("agency.customer.get", { customerId: form.customerId })).result.policies });
  const list = policies ?? pols.data ?? [];
  const record = useMutation({
    mutationFn: () => callAction<"agency.payment.record", { receiptUrl: string; payment: Payment }>("agency.payment.record", { customerId: form.customerId, policyId: form.policyId || undefined, amountCents: Math.round(Number(form.amount) * 100), method: form.method, purpose: form.purpose, reference: form.reference || undefined, note: form.note || undefined }),
    onSuccess: (r) => {
      toast({ title: `${t("payments.recorded")} · ${r.result.payment.receiptNumber}`, tone: "success" });
      window.open(r.result.receiptUrl, "_blank", "noopener");
      for (const k of ["payments", "customer", "policy-history", "documents"]) qc.invalidateQueries({ queryKey: [k] });
      onClose();
      setForm((f) => ({ ...f, amount: "", reference: "", note: "", policyId: "" }));
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const ok = form.customerId && Number(form.amount) > 0;
  return (
    <Sheet open={open} onClose={onClose} title={t("payments.record")}>
      <form className="space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (ok) record.mutate(); }}>
        {!customerId ? (
          <div>
            <Input label={t("common.name")} value={form.customerQuery} onChange={set("customerQuery")} />
            {search.data?.length ? (
              <ul className="mt-1 rounded-xl border border-ink/10 bg-white text-sm">
                {search.data.map((c) => <li key={c.id}><button type="button" onClick={() => setForm((f) => ({ ...f, customerId: c.id, customerQuery: c.name, policyId: "" }))} className={cn("w-full px-3 py-2 text-left hover:bg-ink/5", form.customerId === c.id && "bg-primary/10 font-semibold")}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</button></li>)}
              </ul>
            ) : null}
          </div>
        ) : null}
        {list.length ? (
          <Select label={t("payments.policy")} value={form.policyId} onChange={set("policyId")}>
            <option value="">{t("payments.noPolicy")}</option>
            {list.map((p) => <option key={p.id} value={p.id}>{p.carrier} {p.line.replace(/_/g, " ")} {p.policyNumber}</option>)}
          </Select>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label={t("payments.amount")} type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} required />
          <Select label={t("payments.method")} value={form.method} onChange={set("method")}>{METHODS.map((m) => <option key={m} value={m}>{t(`payments.method.${m}`)}</option>)}</Select>
          <Select label={t("payments.purpose")} value={form.purpose} onChange={set("purpose")}>{PURPOSES.map((m) => <option key={m} value={m}>{t(`payments.purpose.${m}`)}</option>)}</Select>
          <Input label={t("payments.reference")} value={form.reference} onChange={set("reference")} maxLength={60} />
        </div>
        <Textarea placeholder={t("payments.note")} value={form.note} onChange={set("note")} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" loading={record.isPending} disabled={!ok}><Wallet className="h-4 w-4" />{t("payments.record")}</Button>
        </div>
      </form>
    </Sheet>
  );
}

/** Payments on one customer, for the customer file. */
export function CustomerPayments({ customerId, policies }: { customerId: string; policies: Policy[] }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ["payments", "customer", customerId], queryFn: async () => (await callAction<"agency.payment.list", ListResult>("agency.payment.list", { customerId, includeVoided: true, unremittedOnly: false, limit: 20 })).result });
  const rows = q.data?.payments ?? [];
  return (
    <Card>
      <CardHeader title={t("payments.customerCard")} action={<Button size="sm" variant="outline" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />{t("payments.record")}</Button>} />
      {rows.length ? <ul className="divide-y divide-ink/5">{rows.slice(0, 5).map((r) => <PaymentRow key={r.payment.id} row={r} compact />)}</ul> : <p className="px-5 pb-4 text-sm text-ink/55">{t("common.empty")}</p>}
      <RecordPaymentSheet open={adding} onClose={() => setAdding(false)} customerId={customerId} policies={policies} />
    </Card>
  );
}
